import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import conversionService from '../services/conversionService';
import { 
    convertUTCToMadridDateTimeLocalString, 
    convertMadridDateTimeLocalToUTCString 
} from '../utils/dateUtils';

const EditConversionModal = ({ conversion, isOpen, onClose, onConversionUpdated }) => {
  const [formData, setFormData] = useState({
    gclid: '',
    conversion_event_time: '', // Campo corregido para la fecha/hora en formato local
    conversionValue: '',
    conversionCurrency: 'EUR' // Asumimos EUR o la moneda de la conversión
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (conversion && isOpen) {
      setFormData({
        gclid: conversion.gclid || '',
        // Usar conversion.conversion_event_time
        conversion_event_time: conversion.conversion_event_time 
                        ? convertUTCToMadridDateTimeLocalString(conversion.conversion_event_time)
                        : '',
        conversionValue: conversion.conversionValue !== undefined ? String(conversion.conversionValue) : '',
        conversionCurrency: conversion.currency_code || conversion.conversionCurrency || 'EUR'
      });
      setError(''); // Limpiar errores al abrir/cambiar conversión
    } else if (!isOpen) {
      // Resetear el formulario si el modal se cierra sin guardar o si 'conversion' se vuelve null
      setFormData({
        gclid: '',
        conversion_event_time: '', // Campo corregido
        conversionValue: '',
        conversionCurrency: 'EUR'
      });
      setError('');
    }
  }, [conversion, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!conversion || !conversion.id) {
      setError('No se ha proporcionado una conversión válida para actualizar.');
      toast.error('Error: ID de conversión no encontrado.');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const dataToUpdate = {
        gclid: formData.gclid,
        // Usar conversion_event_time y convertir la fecha local de Madrid de vuelta a UTC ISO String
        conversion_event_time: convertMadridDateTimeLocalToUTCString(formData.conversion_event_time),
        conversionValue: parseFloat(formData.conversionValue),
        currency_code: formData.conversionCurrency,
        // Asegúrate de que el backend espera estos campos y puede actualizarlos.
        // No envíes todo el objeto 'conversion' para evitar sobrescribir otros campos.
      };

      await conversionService.updateConversion(conversion.id, dataToUpdate);
      toast.success('Conversión actualizada con éxito');
      if (onConversionUpdated) {
        onConversionUpdated(); // Llama al callback para refrescar datos en la pantalla padre
      }
      onClose(); // Cierra el modal
    } catch (err) {
      console.error("Error updating conversion:", err);
      const errorMessage = err.response?.data?.message || err.message || 'Error al actualizar la conversión.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !conversion) { // No renderizar si no está abierto o no hay conversión
    return null;
  }

  return (
    <dialog id={`edit_conversion_modal_${conversion.id}`} className="modal modal-open" open>
      <div className="modal-box">
        <button 
          type="button" 
          className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
          onClick={onClose}
          disabled={loading}
        >
          ✕
        </button>
        <h3 className="font-bold text-lg">Editar Conversión (#{conversion.id})</h3>
        
        {error && (
          <div className="alert alert-error my-3 text-sm p-2">
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <label htmlFor={`conversion_event_time-${conversion.id}`} className="label">
              <span className="label-text">Fecha de Conversión</span>
            </label>
            <input 
              type="datetime-local" 
              id={`conversion_event_time-${conversion.id}`}
              name="conversion_event_time"
              value={formData.conversion_event_time}
              onChange={handleChange}
              className="input input-bordered w-full"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label htmlFor={`conversionValue-${conversion.id}`} className="label">
              <span className="label-text">Valor de Conversión</span>
            </label>
            <input 
              type="number"
              id={`conversionValue-${conversion.id}`}
              step="0.01"
              name="conversionValue"
              placeholder="Valor de la conversión"
              value={formData.conversionValue}
              onChange={handleChange}
              className="input input-bordered w-full"
              required
              disabled={loading}
            />
          </div>
          
          <div>
            <label htmlFor={`conversionCurrency-${conversion.id}`} className="label">
              <span className="label-text">Moneda</span>
            </label>
            <input 
              type="text"
              id={`conversionCurrency-${conversion.id}`}
              name="conversionCurrency"
              placeholder="EUR"
              value={formData.conversionCurrency}
              onChange={handleChange}
              className="input input-bordered w-full"
              // Considera si este campo debe ser editable o no.
              // Si se edita, asegúrate que el backend lo procesa correctamente.
              // Por ahora, lo hacemos editable.
              disabled={loading}
            />
          </div>

          <div>
            <label htmlFor={`gclid-${conversion.id}`} className="label">
              <span className="label-text">GCLID (Opcional)</span>
            </label>
            <input 
              type="text"
              id={`gclid-${conversion.id}`}
              name="gclid"
              placeholder="Google Click ID"
              value={formData.gclid}
              onChange={handleChange}
              className="input input-bordered w-full"
              disabled={loading}
            />
          </div>

          <div className="modal-action mt-6">
            <button 
              type="button" 
              className="btn btn-ghost" 
              onClick={onClose} 
              disabled={loading}
            >
              Cancelar
            </button>            
            <button 
              type="submit" 
              className="btn btn-primary" 
              disabled={loading}
            >
              {loading ? <span className="loading loading-spinner loading-xs"></span> : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
      {/* Backdrop para cerrar al hacer clic fuera (estilo DaisyUI) */}
      <form method="dialog" className="modal-backdrop">
        <button type="button" onClick={onClose}>close</button>
      </form>
    </dialog>
  );
};

export default EditConversionModal; 
